import { LiveValue, NetworkError, ProblemError, type HyloServices, type HttpMethod, type RequestOptions } from '@/model';
import type { QueryClient } from '@tanstack/react-query';
import { v7 } from 'uuid';
import { canPost, manages, object, records, teamOf, text } from './domain';
import type { ConversationEnvelope, Fleet, ProposalPage, TurnsPage, TurnRef, WakeDraft } from './types';
import type { HomeAssistantAttention } from './public';
let serial = 0;
const origin = () => typeof location === 'undefined' ? 'same-origin' : location.origin;
export class AgentRuntime {
    readonly scope = new LiveValue({ generation: ++serial, loginGeneration: serial, realmId:'same-origin', accountId: '', enabled: false });
    readonly attention = new LiveValue<HomeAssistantAttention>({ pendingCount: 0, coverage: 'unknown', protectedHomeIds: [] });
    readonly decisions = new DecisionFence();
    readonly stopping = new LiveValue<readonly string[]>([]);
    private stopCommands = new Map<string, symbol>();
    beginStop(id: string) { const token = Symbol(); this.stopCommands.set(id, token); this.stopping.set([...this.stopCommands.keys()]); return () => { if (this.stopCommands.get(id) === token) {
        this.stopCommands.delete(id);
        this.stopping.set([...this.stopCommands.keys()]);
    } }; }
    createChat: CreateChatIntent | undefined;
    readonly introDismissed = new LiveValue(false);
    private readonly introDismissals = new Set<string>();
    readonly cooldowns = new Map<string, number>();
    private backoff = new Map<string, {
        until: number;
        error: ProblemError;
    }>();
    private catalogRefreshes = new Map<string, number[]>();
    private catalogBlocked = new Map<string, number>();
    async refreshCatalog(path: string) {
        const now = Date.now();
        const recent = (this.catalogRefreshes.get(path) ?? []).filter(at => now - at < 60000);
        if (recent.length >= 6 || now < (this.catalogBlocked.get(path) ?? 0))
            return this.request('GET', path, { cause: 'refresh' });
        this.catalogRefreshes.set(path, [...recent, now]);
        try {
            return await this.request('GET', path, { query: { refresh: true }, cause: 'refresh' });
        }
        catch (error) {
            if (error instanceof ProblemError && error.status === 429) {
                this.catalogBlocked.set(path, now + (error.retryAfter ?? 60) * 1000);
                return this.request('GET', path, { cause: 'refresh' });
            }
            throw error;
        }
    }
    readonly intents = new Map<string, FrozenIntent<Record<string, unknown>>>();
    readonly wakeCreates = new Map<string, { draft: WakeDraft; intent: FrozenIntent<Record<string, unknown>> }>();
    private clients = new Map<QueryClient, number>();
    private references = 0;
    private disposers: Array<() => void> = [];
    private abort = new AbortController();
    private queued = false;
    private observing = false;
    private retiredLogins=new Set<string>();
    private loginKey(){const value=this.services.session.lifetime.get();return value?`${value.accountId}:${value.loginId}:${value.generation}`:'';}
    private attentionUsers = 0;
    private attentionTicket = 0;
    private attentionPending = false;
    constructor(readonly services: HyloServices) { this.publishScope(); }
    // Match AssistantModel.introKey: local to this device, Realm and person, not an assistant or setup state.
    private introKey(accountId = this.scope.get().accountId, realm = this.scope.get().realmId) {
        const realmKey = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(realm)
            ? realm : btoa(String.fromCharCode(...new TextEncoder().encode(realm)));
        return `hylo.assistant.introDismissed.${realmKey}.${accountId}`;
    }
    private readIntroDismissal() {
        const key = this.introKey();
        let dismissed = this.introDismissals.has(key);
        try { dismissed ||= localStorage.getItem(key) === 'true'; } catch { /* Keep the current device's in-memory choice if storage is denied. */ }
        this.introDismissed.set(!!this.scope.get().accountId && dismissed);
    }
    dismissIntro() {
        if (!this.scope.get().enabled) return;
        const key = this.introKey();
        this.introDismissals.add(key);
        try { localStorage.setItem(key, 'true'); } catch { /* The current session still dismisses the intro. */ }
        this.readIntroDismissal();
    }
    private forgetIntro(accountId: string, realm = this.scope.get().realmId) {
        const key = this.introKey(accountId, realm);
        this.introDismissals.delete(key);
        try { localStorage.removeItem(key); } catch { /* Account retirement must still finish if storage is denied. */ }
        this.readIntroDismissal();
    }
    /** Services startup owns removal cleanup even when Home and agent routes have never mounted. */
    observeIntroRemoval() {
        let realm = this.realmId() || this.scope.get().realmId;
        const config = this.services.flags.config().subscribe(() => { realm = this.realmId() || realm; });
        const retirement = this.services.session.onRetire(event => {
            if (event.reason === 'remove' || event.reason === 'deleted') this.forgetIntro(event.accountId, realm);
        });
        return () => { config(); retirement(); };
    }
    private realmId(){const config=object(this.services.flags.config().get());const items=object(config.item_context);return text(object(config.realm).id)||text(config.realm_id)||text(items.realm_id)||text(object(items.realm).id);}
    get prefix(){const scope=this.scope.get();return ['hylo',origin(),scope.realmId,scope.accountId,'agents',scope.generation] as const;}
    private publishScope() {
        const state = this.services.session.state.get();
        const phase = this.services.sync.connection.get().phase;
        this.scope.set({ ...this.scope.get(), realmId:this.realmId()||this.scope.get().realmId, accountId: state.phase === 'signedIn' ? state.accountId : '', enabled: state.phase === 'signedIn'&&this.services.session.lifetime.get()?.accountId===state.accountId&&!this.retiredLogins.has(this.loginKey())&&this.services.sync.hasSnapshot.get()&& phase !== 'resyncing' });
        this.readIntroDismissal();
    }
    private retire(clearIntents = true) {
        const prefix = this.prefix;
        this.abort.abort();
        this.abort = new AbortController();
        this.attentionTicket++;
        this.decisions.clear();
        this.stopCommands.clear();
        this.stopping.set([]);
        const generation = ++serial;
        if (clearIntents) {
            this.intents.clear();
            this.wakeCreates.clear();
            this.createChat = undefined;
            this.catalogRefreshes.clear();
            this.catalogBlocked.clear();
            this.backoff.clear();
            this.cooldowns.clear();
        }
        this.scope.set({ ...this.scope.get(), generation, loginGeneration: clearIntents ? generation : this.scope.get().loginGeneration, enabled: false });
        this.attention.set({ pendingCount: 0, coverage: 'unknown', protectedHomeIds: [] });
        for (const client of this.clients.keys()) {
            void client.cancelQueries({ queryKey: prefix });
            client.removeQueries({ queryKey: prefix });
        }
    }
    retain(client?: QueryClient): () => void {
        this.references++;
        if (client)
            this.clients.set(client, (this.clients.get(client) ?? 0) + 1);
        if (!this.observing) {
            this.observing = true;
            this.publishScope();
            const identity = () => { const state = this.services.session.state.get(); return state.phase === 'signedIn' ? state.accountId : state.phase; };
            let previousIdentity = identity();
            let previousPhase = this.services.sync.connection.get().phase;
            let previousLogin=this.loginKey();let previousSnapshot=this.services.sync.hasSnapshot.get();
            const storedIntro = (event: StorageEvent) => {
                if (event.key !== null && !event.key.startsWith('hylo.assistant.introDismissed.')) return;
                if (event.key === null) this.introDismissals.clear();
                else this.introDismissals.delete(event.key);
                this.readIntroDismissal();
            };
            window.addEventListener('storage', storedIntro);
            this.disposers = [() => window.removeEventListener('storage', storedIntro),
                this.services.session.onRetire(()=>{if(previousLogin)this.retiredLogins.add(previousLogin);this.retire();}),
                this.services.session.lifetime.subscribe(()=>{const key=this.loginKey();if(key!==previousLogin){if(previousLogin)this.retiredLogins.add(previousLogin);this.retire();previousLogin=key;}this.publishScope();if(this.attentionUsers)this.refreshAttention();}),
                this.services.sync.hasSnapshot.subscribe(()=>{const ready=this.services.sync.hasSnapshot.get();if(!ready&&previousSnapshot)this.retire(false);previousSnapshot=ready;this.publishScope();if(this.attentionUsers)this.refreshAttention();}),
                this.services.flags.config().subscribe(()=>{const realm=this.realmId();if(realm&&realm!==this.scope.get().realmId)this.retire(this.scope.get().realmId!=='same-origin');this.publishScope();}),
                this.services.session.state.subscribe(() => {
                    const current = identity();
                    if (current !== previousIdentity)
                        this.retire();
                    previousIdentity = current;
                    this.publishScope();
                    if (this.attentionUsers)
                        this.refreshAttention();
                }),
                this.services.sync.connection.subscribe(() => {
                    const phase = this.services.sync.connection.get().phase;
                    if (phase === 'resyncing' && previousPhase !== phase)
                        this.retire(false);
                    previousPhase = phase;
                    this.publishScope();
                    if (this.attentionUsers && phase === 'online')
                        this.refreshAttention();
                }),
                this.services.sync.onUpdate('*', update => {
                    if (update.type === 'item.replica.scope') {
                        this.retire(false);
                        this.publishScope();
                    }
                    if (/^(conversation\.|member\.|project\.|me\.|account\.|studio\.|resource\.|message\.|block\.|contact\.|item\.|calendar\.)/.test(update.type))
                        this.refresh();
                })];
        }
        let released = false;
        return () => {
            if (released)
                return;
            released = true;
            this.references--;
            if (client) {
                const n = (this.clients.get(client) ?? 1) - 1;
                if (n)
                    this.clients.set(client, n);
                else
                    this.clients.delete(client);
            }
            // Keep the login fence alive between screens; no network work runs without subscribers.
        };
    }
    async request<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> {
        const captured = this.scope.get();
        const root = this.abort.signal;
        const rateKey = `${method} ${path} ${options.query?.refresh ? 'refresh' : 'read'}`;
        const rate = this.backoff.get(rateKey);
        if (rate && rate.until > Date.now())
            throw new ProblemError(rate.error.problem, Math.ceil((rate.until - Date.now()) / 1000));
        if (!captured.enabled)
            throw new NetworkError('retired');
        if (method !== 'GET' && this.services.sync.connection.get().phase === 'offline')
            throw new NetworkError('offline');
        const controller = new AbortController();
        const cancel = () => controller.abort();
        const signals = [root, options.signal].filter((s): s is AbortSignal => !!s);
        for (const signal of signals) {
            if (signal.aborted)
                controller.abort();
            else
                signal.addEventListener('abort', cancel, { once: true });
        }
        try {
            const result = await this.services.http.request<T>(method, path, { ...options, signal: controller.signal });
            if (root.aborted || this.scope.get().generation !== captured.generation)
                throw new NetworkError('retired');
            return result;
        }
        catch (error) {
            if (root.aborted || this.scope.get().generation !== captured.generation)
                throw new NetworkError('retired');
            if (error instanceof ProblemError && error.status === 429 && error.retryAfter)
                this.backoff.set(rateKey, { until: Date.now() + error.retryAfter * 1000, error });
            throw error;
        }
        finally {
            for (const signal of signals)
                signal.removeEventListener('abort', cancel);
        }
    }
    refresh() {
        if (this.queued)
            return;
        this.queued = true;
        queueMicrotask(() => {
            this.queued = false;
            for (const client of this.clients.keys())
                void client.invalidateQueries({ queryKey: this.prefix, refetchType: 'active' });
            if (this.attentionUsers)
                this.refreshAttention();
        });
    }
    private readonly attentionResource = { get: () => this.attention.get(), subscribe: (listener: () => void) => {
            this.attentionUsers++;
            const release = this.retain();
            const unsubscribe = this.attention.subscribe(listener);
            this.refreshAttention();
            return () => { unsubscribe(); this.attentionUsers--; release(); };
        } };
    attentionLive() { return this.attentionResource; }
    private refreshAttention() {
        if (this.attentionPending || !this.scope.get().enabled)
            return;
        this.attentionPending = true;
        const ticket = ++this.attentionTicket;
        this.attention.set({ ...this.attention.get(), coverage: 'loading' });
        void (async () => {
            try {
                const fleet = await this.request<Fleet>('GET', '/v1/assistants');
                const entries = fleet.assistants ?? [];
                const pages = await Promise.allSettled(entries.filter(e => e.state === 'ready' && e.account_id).map(e => this.request<ProposalPage>('GET', '/v1/assistant/proposals', { query: { assistant_id: e.account_id!, state: 'pending', limit: 50 } })));
                const peers = await this.request<{
                    approvals: unknown[];
                }>('GET', '/v1/assistant/exchanges/approvals').catch(() => null);
                if (ticket !== this.attentionTicket)
                    return;
                const proposals = pages.flatMap(p => p.status === 'fulfilled' ? p.value.proposals : []);
                const ids = new Set(proposals.filter(p => p.person_id === this.scope.get().accountId && p.state === 'pending').map(p => p.id));
                const partial = pages.some(p => p.status === 'rejected' || !!p.value.next_before) || peers === null;
                this.attention.set({ pendingCount: ids.size + records(peers?.approvals).filter(p => p.state === 'pending' && Date.parse(text(p.expires_at)) > Date.now()).length, coverage: partial ? 'partial' : 'complete', protectedHomeIds: entries.flatMap(e => e.conversation_id ? [e.conversation_id] : []) });
            }
            catch {
                if (ticket === this.attentionTicket)
                    this.attention.set({ ...this.attention.get(), coverage: 'failed' });
            }
            finally {
                this.attentionPending = false;
                if (ticket !== this.attentionTicket && this.attentionUsers && this.scope.get().enabled)
                    this.refreshAttention();
            }
        })();
    }
}
const runtimes = new WeakMap<HyloServices, AgentRuntime>();
export function agentRuntime(services: HyloServices) {
    let r = runtimes.get(services);
    if (!r) {
        r = new AgentRuntime(services);
        runtimes.set(services, r);
    }
    return r;
}
export class FrozenIntent<T extends Record<string, unknown>> {
    readonly body: T & {
        client_id: string;
    };
    private answer: unknown;
    private accepted = false;
    private pending: Promise<unknown> | undefined;
    constructor(body: T) { this.body = structuredClone({ ...body, client_id: typeof body.client_id === 'string' ? body.client_id : v7() }); }
    async run<R>(perform: (body: T & {
        client_id: string;
    }) => Promise<R>): Promise<R> {
        if (this.accepted)
            return this.answer as R;
        if (this.pending)
            return this.pending as Promise<R>;
        const pending = perform(this.body);
        this.pending = pending;
        try {
            const answer = await pending;
            this.answer = answer;
            this.accepted = true;
            return answer;
        }
        finally {
            this.pending = undefined;
        }
    }
}
export class CreateChatIntent {
    private intent: FrozenIntent<Record<string, unknown>>;
    private result: {
        id: string;
        warning?: string;
    } | undefined;
    constructor(body: Record<string, unknown>, private title: string, readonly definitionName?: string) { this.intent = new FrozenIntent(body); }
    get choices() { const { client_id: _clientId, ...choices } = this.intent.body; return { name: this.definitionName, chat_name: this.title, ...choices }; }
    async run(runtime: AgentRuntime) {
        const generation = runtime.scope.get().generation;
        if (this.result)
            return this.result;
        const answer = await this.intent.run(body => runtime.request<ConversationEnvelope>('POST', '/v1/claw/chats', { body }));
        const id = answer.conversation.id;
        this.result = { id, ...(answer.workspace_warning ? { warning: answer.workspace_warning } : {}) };
        try {
            await runtime.services.conversations.ensure(id);
        }
        catch {
            this.result.warning = 'refresh';
        }
        if (runtime.scope.get().generation !== generation)
            throw new NetworkError('retired');
        if (this.title.trim())
            try {
                await runtime.services.conversations.update(id, { title: this.title.trim() });
            }
            catch {
                this.result.warning = 'rename';
            }
        if (runtime.scope.get().generation !== generation)
            throw new NetworkError('retired');
        runtime.refresh();
        return this.result;
    }
}
export class DecisionFence {
    private rejected = new Map<string, number>();
    private approvalRejected = new Map<string, number>();
    private busy = new Map<string, symbol>();
    clear() { this.rejected.clear(); this.approvalRejected.clear(); this.busy.clear(); }
    blocked(id: string, revision: number, kind: 'approval' | 'dismiss' | 'edit' = 'approval') { return this.rejected.get(id) === revision || kind === 'approval' && this.approvalRejected.get(id) === revision || this.busy.has(id); }
    async run<T>(id: string, revision: number, perform: () => Promise<T>, kind: 'approval' | 'dismiss' | 'edit' = 'approval'): Promise<T> {
        if (this.blocked(id, revision, kind))
            throw new NetworkError('review_required');
        const token = Symbol();
        this.busy.set(id, token);
        try {
            return await perform();
        }
        catch (error) {
            if (this.busy.get(id) === token && error instanceof ProblemError && (error.status === 400 || error.status === 409) && !(kind === 'edit' && error.status === 400)) {
                const source = ['item_revision_mismatch', 'mutation_epoch_mismatch', 'capability_required', 'invalid_request'].includes(error.code) || error.status === 400;
                (source ? this.approvalRejected : this.rejected).set(id, revision);
            }
            throw error;
        }
        finally {
            if (this.busy.get(id) === token)
                this.busy.delete(id);
        }
    }
}

export function traceReadable(conversation:ConversationEnvelope['conversation']|undefined,viewer:string){
 const range=object(conversation?.me);return !!conversation&&canPost(conversation)&&(range.history_from_seq??range.from_seq)===0&&range.to_seq==null&&(manages(conversation)||object(conversation.team).created_by===viewer);
}
/** Native Last Turn semantics: all pages, final matching call, then a fresh authority/run check. */
export async function resolveLastTurn(runtime:AgentRuntime,conversationId:string,accountId?:string,signal?:AbortSignal):Promise<TurnRef|undefined>{
 const generation=runtime.scope.get().generation;const viewer=runtime.scope.get().accountId;
 const before=await runtime.request<ConversationEnvelope>('GET',`/v1/conversations/${conversationId}`,{signal});
 if(!traceReadable(before.conversation,viewer))throw new ProblemError({status:403,code:'forbidden'});
 const run=teamOf(before.conversation)?.run_id;if(!run)return undefined;
 let since=0;let last:TurnRef|undefined;
 while(true){
  if(signal?.aborted||runtime.scope.get().generation!==generation)throw new NetworkError('retired');
  const page:TurnsPage=await runtime.request<TurnsPage>('GET',`/v1/claw/chats/${conversationId}/runs/${run}/turns`,{query:{since,limit:200},signal});
  if(page.run_id!==run||!page.tracing.available)throw new NetworkError('trace_unavailable');let cursor:number=since;
  for(const turn of page.turns){if(!Number.isSafeInteger(turn.seq)||turn.seq<=cursor||!turn.member||!Number.isSafeInteger(turn.turn)||turn.turn<0||turn.turn>2147483647||!Number.isSafeInteger(turn.call)||turn.call<1||turn.call>100000)throw new NetworkError('invalid_trace');cursor=turn.seq;if(!accountId||turn.account_id===accountId)last={run,member:turn.member,turn:turn.turn,call:turn.call};}
  if(page.next_since!==cursor||page.has_more&&cursor<=since)throw new NetworkError('invalid_trace');if(!page.has_more)break;since=cursor;
 }
 if(signal?.aborted||runtime.scope.get().generation!==generation)throw new NetworkError('retired');
 const current=await runtime.request<ConversationEnvelope>('GET',`/v1/conversations/${conversationId}`,{signal});
 if(!traceReadable(current.conversation,viewer)||teamOf(current.conversation)?.run_id!==run)throw new ProblemError({status:409,code:'run_changed'});
 return last;
}
